import { useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { publicApi } from '@/lib/api';
import { enrichHours, formatDaySlots } from '@/lib/hours';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { MapPin, Phone, Clock, CreditCard, Bus, CalendarIcon, Send } from 'lucide-react';
import { format } from 'date-fns';
import { fr as frLocale } from 'date-fns/locale';
import { enUS } from 'date-fns/locale';
import { es as esLocale } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';

export default function Reservation() {
  const [settings, setSettings] = useState({});
  const [hours, setHours] = useState([]);
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', time: '', guests: '2', message: '' });
  const [gdprConsent, setGdprConsent] = useState(false);
  const { t, lang } = useLanguage();
  const calendarLocale = lang === 'en' ? enUS : lang === 'es' ? esLocale : frLocale;

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps -- One-time init on mount; stable module-level API refs
    publicApi.getSettings().then(r => setSettings(r.data)).catch(() => {}).finally(() => setSettingsLoaded(true));
    publicApi.getHours().then(r => setHours(enrichHours(r.data.hours))).catch(() => setHours(enrichHours([])));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!gdprConsent) { toast.error("Veuillez accepter le traitement de vos données."); return; }
    if (!date) { toast.error(t('reservation.errorDate')); return; }
    if (!form.time) { toast.error(t('reservation.errorTime')); return; }
    setLoading(true);
    try {
      await publicApi.createReservation({
        ...form,
        date: format(date, 'yyyy-MM-dd'),
        guests: parseInt(form.guests),
      });
      toast.success(t('reservation.success'));
      setForm({ name: '', email: '', phone: '', time: '', guests: '2', message: '' });
      setDate(null);
    } catch {
      toast.error(t('reservation.errorGeneric'));
    }
    setLoading(false);
  };

  const isPhoneMode = settings.reservation_mode === 'phone';
  const reservationsEnabled = settings.reservationsModuleEnabled === true;

  if (!settingsLoaded) {
    return (
      <div className="min-h-screen bg-cream" data-testid="reservation-page-loading">
        <Navbar />
        <div className="pt-28 pb-20 flex justify-center">
          <div className="w-10 h-10 border-4 border-garden-800 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!reservationsEnabled) {
    return (
      <div className="min-h-screen bg-cream" data-testid="reservation-page-disabled">
        <Navbar />
        <div className="pt-28 pb-20 max-w-2xl mx-auto px-4 text-center">
          <Phone size={48} className="mx-auto text-garden-400 mb-4" />
          <h1 className="text-3xl font-heading font-bold text-garden-800 mb-4">Reservations</h1>
          <p className="text-charcoal/60 mb-6">La reservation en ligne n'est pas disponible pour le moment.</p>
          <p className="text-charcoal/60">Vous pouvez nous contacter par telephone au 09 52 67 52 89.</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream" data-testid="reservation-page">
      <Navbar />
      <div className="pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <span className="text-sm font-medium tracking-wide uppercase text-garden-500 mb-2 block">{t('reservation.sectionLabel')}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-garden-800 mb-4">
            {isPhoneMode ? t('reservation.titlePhone') : t('reservation.titleOnline')}
          </h1>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Phone mode OR Form */}
            {isPhoneMode ? (
              <div className="bg-white rounded-3xl p-8 border border-garden-800/5 shadow-sm flex flex-col items-center text-center" data-testid="reservation-phone-mode">
                <div className="w-20 h-20 bg-garden-100 rounded-full flex items-center justify-center mb-6">
                  <Phone size={32} className="text-garden-800" />
                </div>
                <h2 className="font-heading font-semibold text-2xl text-garden-800 mb-4">
                  {t('reservation.phoneOnly')}
                </h2>
                <p className="text-charcoal/70 mb-6 max-w-md">
                  {t('reservation.phoneDesc')}
                </p>
                {settings.phone && (
                  <>
                    <p className="text-3xl font-heading font-bold text-garden-800 mb-4" data-testid="phone-number-display">
                      {settings.phone}
                    </p>
                    <a
                      href={`tel:${settings.phone.replace(/\s/g, '')}`}
                      className="inline-flex items-center gap-2 bg-garden-800 hover:bg-garden-900 text-white font-medium px-8 py-3 rounded-full transition-colors text-lg"
                      data-testid="phone-call-btn"
                    >
                      <Phone size={20} /> {t('reservation.callNow')}
                    </a>
                  </>
                )}
                {!settings.phone && (
                  <p className="text-amber-600 text-sm">{t('reservation.noPhone')}</p>
                )}
              </div>
            ) : (
            <div className="bg-white rounded-3xl p-8 border border-garden-800/5 shadow-sm">
              <h2 className="font-heading font-semibold text-2xl text-garden-800 mb-6">{t('reservation.formTitle')}</h2>
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="reservation-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-charcoal text-sm mb-1 block">{t('reservation.name')} *</Label>
                    <Input id="name" data-testid="reservation-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="border-garden-200 focus:border-garden-800 rounded-xl" placeholder={t('reservation.namePlaceholder')} />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-charcoal text-sm mb-1 block">{t('reservation.phone')} *</Label>
                    <Input id="phone" data-testid="reservation-phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} required className="border-garden-200 focus:border-garden-800 rounded-xl" placeholder={t('reservation.phonePlaceholder')} />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email" className="text-charcoal text-sm mb-1 block">{t('reservation.email')} *</Label>
                  <Input id="email" type="email" data-testid="reservation-email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required className="border-garden-200 focus:border-garden-800 rounded-xl" placeholder={t('reservation.emailPlaceholder')} />
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-charcoal text-sm mb-1 block">{t('reservation.date')} *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" data-testid="reservation-date" className="w-full justify-start text-left font-normal border-garden-200 rounded-xl h-10">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {date ? format(date, 'dd/MM/yyyy') : t('reservation.choose')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-white" align="start">
                        <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date()} locale={calendarLocale} />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label className="text-charcoal text-sm mb-1 block">{t('reservation.time')} *</Label>
                    <Select value={form.time} onValueChange={(v) => setForm({...form, time: v})}>
                      <SelectTrigger data-testid="reservation-time" className="border-garden-200 rounded-xl">
                        <SelectValue placeholder={t('reservation.time')} />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {['11:30','12:00','12:30','13:00','13:30','14:00','19:00','19:30','20:00','20:30','21:00','21:30','22:00'].map(tt => (
                          <SelectItem key={tt} value={tt}>{tt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-charcoal text-sm mb-1 block">{t('reservation.guests')}</Label>
                    <Select value={form.guests} onValueChange={(v) => setForm({...form, guests: v})}>
                      <SelectTrigger data-testid="reservation-guests" className="border-garden-200 rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {[1,2,3,4,5,6,7,8].map(n => (
                          <SelectItem key={n} value={String(n)}>{n} {n > 1 ? t('reservation.guestPlural') : t('reservation.guestSingular')}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="message" className="text-charcoal text-sm mb-1 block">{t('reservation.message')}</Label>
                  <Textarea id="message" data-testid="reservation-message" value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="border-garden-200 focus:border-garden-800 rounded-xl" placeholder={t('reservation.messagePlaceholder')} rows={3} />
                </div>
                <div className="flex items-start gap-2">
                  <input id="gdpr-consent" type="checkbox" checked={gdprConsent} onChange={(e) => setGdprConsent(e.target.checked)} className="mt-1 h-4 w-4 rounded border-garden-300 text-garden-800 focus:ring-garden-800" />
                  <Label htmlFor="gdpr-consent" className="text-sm text-charcoal cursor-pointer">
                    J'accepte que mes données soient traitées pour la gestion de ma demande conformément aux <Link to="/mentions-legales" className="text-garden-800 underline hover:text-garden-900">Mentions Légales du site</Link>.
                  </Label>
                </div>
                <Button type="submit" data-testid="reservation-submit" disabled={loading} className="w-full bg-garden-800 hover:bg-garden-900 text-white rounded-full py-6 text-lg font-medium">
                  {loading ? t('reservation.sending') : <><Send size={18} className="mr-2" /> {t('reservation.submit')}</>}
                </Button>
              </form>
            </div>
            )}

            {/* Info Column */}
            <div className="space-y-6">
              {/* Map */}
              <div className="rounded-2xl overflow-hidden shadow-sm border border-garden-200 h-[250px]">
                <iframe
                  title="Côté Terrasse Bordeaux"
                  src="https://maps.google.com/maps?q=198+Cours+de+l+Argonne+33000+Bordeaux&t=&z=17&ie=UTF8&iwloc=&output=embed"
                  width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy"
                />
              </div>

              {/* Contact Info */}
              <div className="bg-white rounded-2xl p-6 border border-garden-800/5 shadow-sm">
                <h3 className="font-heading font-semibold text-lg text-garden-800 mb-4">{t('reservation.infoTitle')}</h3>
                <div className="space-y-4">
                  <a href={settings.maps_url || '#'} target="_blank" rel="noreferrer" className="flex items-start gap-3 text-charcoal hover:text-garden-800 transition-colors" data-testid="info-address">
                    <MapPin size={18} className="text-garden-500 mt-0.5 shrink-0" />
                    <span className="text-sm">{settings.address || '198 Cours de l\'Argonne, 33000 Bordeaux'}</span>
                  </a>
                  <a href="tel:0952675289" className="flex items-center gap-3 text-charcoal hover:text-garden-800 transition-colors" data-testid="info-phone">
                    <Phone size={18} className="text-garden-500 shrink-0" />
                    <span className="text-sm">{settings.phone || '09 52 67 52 89'}</span>
                  </a>
                  <div className="flex items-start gap-3" data-testid="info-payment">
                    <CreditCard size={18} className="text-garden-500 mt-0.5 shrink-0" />
                    <span className="text-sm">{settings.payment_methods?.join(', ') || 'Carte bancaire, Espèces, Tickets restaurant'}</span>
                  </div>
                  <div className="flex items-start gap-3" data-testid="info-access">
                    <Bus size={18} className="text-garden-500 mt-0.5 shrink-0" />
                    <span className="text-sm">{settings.access_info || 'Tram C - Arrêt Tauzia / Bus 9 et 34'}</span>
                  </div>
                </div>
              </div>

              {/* Hours */}
              <div className="bg-white rounded-2xl p-6 border border-garden-800/5 shadow-sm">
                <h3 className="font-heading font-semibold text-lg text-garden-800 mb-4 flex items-center gap-2">
                  <Clock size={18} className="text-garden-500" /> {t('reservation.openingHours')}
                </h3>
                <div className="space-y-2">
                  {hours.map((h) => (
                    <div key={h.day} className="flex justify-between text-sm" data-testid={`hours-day-${h.day}`}>
                      <span className="font-medium text-charcoal">{h.day_name}</span>
                      <span className={h.is_closed ? 'text-red-500' : 'text-charcoal/70'}>
                        {h.is_closed ? t('reservation.closed') : formatDaySlots(h.slots)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
